import React from "react";
import { motion } from "framer-motion";
import { FaArrowRight, FaCarSide, FaMapMarkerAlt, FaPhoneAlt, FaTools } from "react-icons/fa";
import backgroundImage from "../assets/home3.png";

const dispatchItems = [
  {
    label: "Roadside call",
    detail: "Tow, jump-start, tyre help",
    status: "24/7 intake",
    icon: FaPhoneAlt,
  },
  {
    label: "Garage match",
    detail: "Verified teams near your route",
    status: "Kampala first",
    icon: FaMapMarkerAlt,
  },
  {
    label: "Service record",
    detail: "Diagnosis, quote, payment, review",
    status: "In-app trail",
    icon: FaTools,
  },
];

export default function HeroSection() {
  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-[#111714] text-white">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-[linear-gradient(90deg,rgba(17,23,20,0.96)_0%,rgba(17,23,20,0.78)_45%,rgba(17,23,20,0.38)_100%)]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-7xl flex-col justify-center px-6 pb-14 pt-32 sm:px-8 lg:px-12">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.75fr)]">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <p className="mb-5 inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#D6F7C6] backdrop-blur">
              Kampala launch network
            </p>
            <h1 className="max-w-4xl text-5xl font-black leading-[0.95] text-white sm:text-6xl lg:text-7xl">
              Road-ready service for drivers who cannot wait.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/82">
              Carmie connects vehicle owners with verified garages, towing teams,
              and roadside support so a breakdown becomes a managed job, not a
              guessing game.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() =>
                  document.getElementById("get-the-app")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
                className="inline-flex items-center justify-center gap-3 rounded-md bg-[#8FE85E] px-6 py-4 text-base font-extrabold text-[#0F1A12] shadow-[0_16px_40px_rgba(143,232,94,0.24)] transition hover:bg-[#A8F376] focus:outline-none focus:ring-2 focus:ring-[#F4C44D] focus:ring-offset-2 focus:ring-offset-[#111714]"
              >
                Get the app update
                <FaArrowRight aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() =>
                  document.getElementById("join")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
                className="inline-flex items-center justify-center rounded-md border border-white/30 px-6 py-4 text-base font-bold text-white transition hover:border-[#8FE85E] hover:text-[#D6F7C6] focus:outline-none focus:ring-2 focus:ring-[#F4C44D] focus:ring-offset-2 focus:ring-offset-[#111714]"
              >
                Join the garage network
              </button>
            </div>

            <div className="mt-10 grid max-w-3xl gap-3 text-sm text-white/76 sm:grid-cols-3">
              <div className="border-l-2 border-[#8FE85E] pl-4">
                <span className="block text-xl font-black text-white">01</span>
                Request help from the road.
              </div>
              <div className="border-l-2 border-[#F4C44D] pl-4">
                <span className="block text-xl font-black text-white">02</span>
                Get matched to a verified provider.
              </div>
              <div className="border-l-2 border-white/35 pl-4">
                <span className="block text-xl font-black text-white">03</span>
                Track the service through completion.
              </div>
            </div>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, x: 22 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, delay: 0.1, ease: "easeOut" }}
            className="w-full max-w-md justify-self-center rounded-lg border border-white/16 bg-[#F7F8EF]/95 p-4 text-[#101712] shadow-2xl lg:justify-self-end"
            aria-label="Carmie service board"
          >
            <div className="flex items-center justify-between border-b border-[#243126]/15 pb-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-[#49624B]">
                  Carmie board
                </p>
                <h2 className="mt-1 text-2xl font-black">Live service path</h2>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#111714] text-[#8FE85E]">
                <FaCarSide aria-hidden="true" />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {dispatchItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className="grid grid-cols-[44px_minmax(0,1fr)] gap-3 rounded-md border border-[#243126]/10 bg-white px-3 py-4"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#E8F8DC] text-[#254E20]">
                      <Icon aria-hidden="true" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-black">{item.label}</h3>
                        <span className="rounded-full bg-[#F7E6A8] px-2 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-[#5A4310]">
                          {item.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm leading-6 text-[#516052]">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 rounded-md bg-[#111714] p-4 text-white">
              <p className="text-sm font-bold text-[#D6F7C6]">
                Built for local roads, real garages, and drivers who need a
                clear next step.
              </p>
            </div>
          </motion.aside>
        </div>
      </div>
    </div>
  );
}
